import { ChangeDetectorRef, Component, computed, effect, Inject, inject, OnInit, signal, untracked } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ToteTransactionService } from '../../../services/tote-transaction-service';
import { GetMarketGeneratorsResponse } from '../../../interfaces/generatorsmodel';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatCardModule } from '@angular/material/card';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { FeedstockService } from '../../../services/feedstockservice';
import { IdName } from '../../../interfaces/pricematrixmodel';
import { forkJoin, Subject, takeUntil } from 'rxjs';
import { ToteTransactionByIdRecord, ToteTransactionInputData, SaveToteTransactionPayload, ToteTransactionFeeResponse } from '../../../interfaces/tote-transaction-record';
import { MatIconModule } from '@angular/material/icon';
import { NotificationService } from '../../../services/notificationservice';
import { MatDialogModule } from '@angular/material/dialog';
import e from 'express';
import { numericInputLimit } from '../../shared/commonutils/helper';
import { DragDropModule } from '@angular/cdk/drag-drop';
@Component({
  selector: 'app-add-edit-tote-transaction',
  imports: [CommonModule, MatButtonModule, MatFormFieldModule, MatSelectModule, MatInputModule,
    MatCardModule, ReactiveFormsModule, MatDatepickerModule, MatIconModule, 
    MatDialogModule,DragDropModule],
  templateUrl: './add-edit-tote-transaction.component.html',
  styleUrl: './add-edit-tote-transaction.component.scss'
})
export class AddEditToteTransactionComponent implements OnInit {

  // Form Builder
  toteTxForm!: FormGroup;

  // Mode and guards
  mode = signal('');

  // Controls when effects are allowed to run
  isReady = signal(false);

  // Tracks user-initiated changes (Edit / Duplicate)
  userInteracted = signal(false);

  //Source Data 
  generators = signal<GetMarketGeneratorsResponse[]>([]);
  streams = signal<IdName[]>([]);
  transactionTypes = signal<IdName[]>([]);
  transactionStatues = signal<IdName[]>([]);
  toteTransactionByIdRecord!: ToteTransactionByIdRecord;

  //Search Signals
  generatorSearch = signal('');
  streamSearch = signal('');

  //Signal mirror
  generatorId = signal<number | null>(null);
  streamId = signal<number | null>(null);
  transactionTypeId = signal<number | null>(null);

  //Defaults
  readonly OTHER_TRANSACTION_TYPE_ID = 8;
  canSave: boolean = false;
  //For unsubscribing observables
  private destroy$ = new Subject<void>();

  constructor(
    public _dialogRef: MatDialogRef<AddEditToteTransactionComponent>,
    @Inject(MAT_DIALOG_DATA) public _data: ToteTransactionInputData,
    private _fb: FormBuilder,
    private _toteTxSvc: ToteTransactionService,
    private _feedStockSvc: FeedstockService,
    private _notificationSvc: NotificationService, private cdref: ChangeDetectorRef) {
    //Form Initialization
    this.initializeForm();
    this.bindFormSignals();
  }
  ngOnInit(): void {

    //Load lookups
    this.loadLookups();
    this.canSave = this._data.canSave;
    this.mode.set(this._data.mode);
    this.generators.set(this._data.generators);

    if (this.mode() === 'Edit' || this.mode() === 'Duplicate') {
      //Fetch tote transaction by id
      this.getToteTransactionById();
    }
    else {
      this.isReady.set(true); //ADD mode ready immediately
    }

  }


  private bindFormSignals(): void {
    this.toteTxForm.get('customerID')!
      .valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(v => {
        this.generatorId.set(v);
        this.userInteracted.set(true);
      });

    this.toteTxForm.get('streamID')!
      .valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(v => {
        this.streamId.set(v);
        this.userInteracted.set(true);
      });

    this.toteTxForm.get('transactionTypeId')!
      .valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(v => {
        this.transactionTypeId.set(v);
        this.userInteracted.set(true);
      });
  }

  // Initialize the form
  initializeForm(): void {
    this.toteTxForm = this._fb.group({
      Id: [this._data.id],
      customerID: [null, Validators.required],
      streamID: [null, Validators.required],
      transactionTypeId: [null, Validators.required],
      transactionDate: [null, Validators.required],
      totePriceMatrixId: [null, Validators.required],
      notes: [null, Validators.maxLength(250)],
      fee: [{ value: null, disabled: true }, [Validators.required, Validators.max(999999)]],
      transactionStatusId: [null, Validators.required]
    });
  }

  // load lookup data for dropdowns
  private loadLookups() {
    forkJoin({
      txStatues: this._toteTxSvc.getToteTransactionStatuses(),
      txTypes: this._toteTxSvc.getToteTransactionTypes()
    })
      .pipe(
        takeUntil(this.destroy$)
      )
      .subscribe(({ txStatues, txTypes }) => {
        this.generators.set(this._data.generators);
        this.transactionTypes.set(txTypes);
        this.transactionStatues.set(txStatues);
      });
  }

  // Fetch tote transaction by ID for edit mode and untracked 
  // the changes for restricting duplicate calls

  private getToteTransactionById(): void {

    this._toteTxSvc.getToteTransactionById(this._data.id!).pipe(takeUntil(this.destroy$)).subscribe({
      next: (toteTxRecord: ToteTransactionByIdRecord) => {
        this.toteTransactionByIdRecord = toteTxRecord;

        //If mode is duplicate, clear the ID
        if (this.mode() === 'Duplicate') {
          this.toteTransactionByIdRecord.id = 0;
          this.toteTxForm?.markAsDirty();
        }

        //Patch the non dependent form values
        this.toteTxForm.patchValue({
          Id: this.toteTransactionByIdRecord.id,
          transactionDate: this.toteTransactionByIdRecord.transactionDate,
          customerID: this.toteTransactionByIdRecord.customerID,
          streamID: this.toteTransactionByIdRecord.streamID,
          transactionTypeId: this.toteTransactionByIdRecord.transactionTypeId,
          totePriceMatrixId: this.toteTransactionByIdRecord.totePriceMatrixId,
          notes: this.toteTransactionByIdRecord.notes,
          fee: this.toteTransactionByIdRecord.fee,
          transactionStatusId: this.toteTransactionByIdRecord.transactionStatusId
        }, { emitEvent: false });

        this._feedStockSvc.getToteStreamList(this.toteTransactionByIdRecord.customerID!.toString()).pipe(takeUntil(this.destroy$)).subscribe({
          next: (streams: IdName[]) => {
            this.streams.set(streams);

            //Sync signals once (no effects yet)
            this.generatorId.set(this.toteTransactionByIdRecord.customerID);
            this.streamId.set(this.toteTransactionByIdRecord.streamID);
            this.transactionTypeId.set(this.toteTransactionByIdRecord.transactionTypeId);
            
             // ✅ NOW effects are allowed
            this.isReady.set(true); //Now we have all the data to enable the form

          }
        })
      }
    })
  }



  private canReact(): boolean {
    return (
      this.isReady() &&
      (this.mode() === 'Add'|| this.mode() === 'Edit' || this.mode() === 'Duplicate' || this.userInteracted())
    );
  }



  onGeneratorChange(event: any): void {
    this.toteTxForm.get('streamID')!.setValue(null);
    this.toteTxForm.get('transactionTypeId')!.setValue(null);
    this.toteTxForm.get('fee')!.setValue(null);
  }

  onStreamChange(event: any): void {
    this.toteTxForm.get('transactionTypeId')!.setValue(null);
    this.toteTxForm.get('fee')!.setValue(null);
  }

  // Stream Load Effects ( ADD + EDIT + DUPLICATE )
  loadStreamEffect = effect(() => {
    if (!this.canReact()) return;

    const genId = this.generatorId();
    const currentStreamId = untracked(() => this.streamId());
    if (!genId) {
      if (!this.toteTxForm.get('customerID')?.value) {
        this.streams.set([]);
        this.toteTxForm.get('streamID')?.reset();
        if(this.userInteracted()) {
          this.setFeeAndPriceMatrix(null, null);
        }
      }
     return;
    }

    //get the streams based on generator
    this._feedStockSvc.getToteStreamList(genId.toString()).pipe(takeUntil(this.destroy$)).subscribe({
      next: (streams: IdName[]) => {
        this.streams.set(streams);
        if(this.userInteracted()) {
          this.setFeeAndPriceMatrix(null, null);
        }
      }
    })

  });

  caclulateFeeFffect = effect(() => {

    if (!this.canReact()) return;

    //Geting signal values
    const genId = this.generatorId() ?? this.toteTxForm.get('customerID')?.value;
    const strmId = this.streamId() ?? this.toteTxForm.get('streamID')?.value;
    const txTypeId = this.transactionTypeId() ?? this.toteTxForm.get('transactionTypeId')?.value;
    if (txTypeId === this.OTHER_TRANSACTION_TYPE_ID) {
      return; 
    }
    //Skip the call if no values assigned
    if (!genId || !strmId || !txTypeId) {
      untracked(() => this.setFeeAndPriceMatrix(null, null));
      return;
    }
    const transactionDate = new Date(this.toteTxForm.get('transactionDate')!.value).toISOString().slice(0, 10);

    if (genId && strmId && txTypeId && transactionDate) {
      this._toteTxSvc.getFee(txTypeId, genId, strmId, transactionDate).pipe(takeUntil(this.destroy$)).subscribe({
        next: (response: ToteTransactionFeeResponse) => {

          // Case 1: No price matrix found
          if (response.priceMatrixId === 0) {
            this._notificationSvc.show('Price matrix not found', 'error');
            this.setFeeAndPriceMatrix(null, null);
            return;
          }

          // Case 2: Price matrix found but fee is empty/zero
          if (response.fee === 0) {
            // Special rule for "Other" transaction type: keep the matrix ID, clear fee
            if (txTypeId === this.OTHER_TRANSACTION_TYPE_ID) {
              this.setFeeAndPriceMatrix(null, response.priceMatrixId);
              return;
            }

            // Otherwise, notify and clear both
            this._notificationSvc.show('Price Matrix fee missing', 'error');
            this.setFeeAndPriceMatrix(null, null);
            return;
          }
          // Case 3: Valid fee and matrix—apply both
          this.setFeeAndPriceMatrix(response.fee, response.priceMatrixId);
        }
      });
    }
  });

  // Helper: centralize form updates
  private setFeeAndPriceMatrix(fee: number | null, priceMatrixId: number | null): void {
    this.toteTxForm.patchValue({
      fee,
      totePriceMatrixId: priceMatrixId
    }, { emitEvent: false }); // prevent valueChanges cascades if not needed
  }


  enableDisableTransactionTypeEffect = effect(() => {
    if (!this.canReact()) return;
    const transactionId = this.transactionTypeId();
    if (!transactionId) {
      return;
    }

    if (transactionId == this.OTHER_TRANSACTION_TYPE_ID) {
      this.toteTxForm.get('fee')!.enable({ emitEvent: false });
      this.toteTxForm.get('totePriceMatrixId')!.clearValidators();
      const currentVal = this.toteTxForm.get('fee')?.value ?? this.toteTransactionByIdRecord?.fee;
      if (currentVal !== null && currentVal !== undefined) {        
        this.toteTxForm.get('fee')!.setValue(currentVal, { emitEvent: false });
        this.cdref.detectChanges(); 
      }
      this.toteTxForm.get('totePriceMatrixId')!.updateValueAndValidity();
    }
    else {
      this.toteTxForm.get('fee')!.disable({ emitEvent: false });
      this.toteTxForm.get('totePriceMatrixId')!.setValidators(Validators.required);
      this.toteTxForm.get('totePriceMatrixId')!.updateValueAndValidity();

    }
  });

  //Search Handlers
  onGenSearchQuery(event: Event): void {
    const searchTerm = (event.target as HTMLInputElement).value.toLowerCase();
    this.generatorSearch.set(searchTerm);
  }
  onStreamSearchQuery(event: Event): void {
    const searchTerm = (event.target as HTMLInputElement).value.toLowerCase();
    this.streamSearch.set(searchTerm);
  }

  onStreamDropdownOpen(): void {
    this.streamSearch.set('');
  }

  //Filters
  filteredGenerators = computed(() => {
    const search = this.generatorSearch().toLowerCase();
    return this.generators().filter(g => g.customerName.toLowerCase().includes(search));
  });

  filteredStreams = computed(() => {
    const search = this.streamSearch().toLowerCase();
    return this.streams().filter(s => s.name.toLowerCase().includes(search));
  });

  //save tote transaction
  onSubmit() {
    if (!this.toteTxForm || this.toteTxForm.invalid) {
      this.toteTxForm?.markAllAsTouched();
      return;
    }

    const raw = this.toteTxForm.getRawValue();

    const payload: SaveToteTransactionPayload = {
      id: raw.Id ?? null,
      customerID: raw.customerID,
      streamID: raw.streamID,
      transactionDate: new Date(raw.transactionDate).toISOString()!.slice(0, 10),
      transactionStatusId: raw.transactionStatusId,
      transactionTypeId: raw.transactionTypeId,
      totePriceMatrixId: raw.totePriceMatrixId,
      fee: raw.fee!,
      notes: raw.notes
    };
    console.log('Submitting Tote Transaction with payload:', payload);
    //Save Tote Transaction
    this._toteTxSvc.saveToteCustomerTransaction(payload).pipe(takeUntil(this.destroy$)).subscribe({
      next: (status: boolean) => {
        if (status) {
          this._notificationSvc.show('Tote transaction saved successfully', 'success');
          this._dialogRef.close(status);
        }
      }
    });
  }

  getHeaderText(): string {
    if (this.mode() === 'Add') {
      return 'Add Tote Transaction';
    } else if (this.mode() === 'Edit') {
      return 'Edit Tote Transaction (id: ' + this.toteTransactionByIdRecord?.id + ')';
    } else if (this.mode() === 'Duplicate') {
      return 'Duplicate Tote Transaction';
    } else {
      return '';
    }
  }

  //Cancel dialog
  cancel(): void {
    this._dialogRef.close(false);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  roundOff(event: Event, maxValue: number, maxDecimals: number, controlName: string): void {
    const input = event.target as HTMLInputElement;
    let val = input.value;
    
    val = numericInputLimit(event, maxValue, maxDecimals);
    
    input.value = val;

    // 6. Parsing for the backing variables    
    const isValueEmpty = val === '' || val === '.';
    if (isValueEmpty) {
      this.toteTxForm.get(controlName)?.setValue(null, { emitEvent: true });
    } else if (val.endsWith('.')) {
      this.toteTxForm.get(controlName)?.setErrors({ 'incompleteDecimal': true });
    } else {      
      const parsedValue = parseFloat(val);
      this.toteTxForm.get(controlName)?.setValue(parsedValue, { emitEvent: true });
    }

    const feeControl = this.toteTxForm.get('fee');

    // Mark both the control AND the group as dirty
    feeControl?.markAsDirty();
    this.toteTxForm.markAsDirty(); 
    
    feeControl?.updateValueAndValidity();
    this.toteTxForm.updateValueAndValidity();
    
    this.cdref.detectChanges();

    requestAnimationFrame(() => {
      if (document.activeElement === input) {
        input.value = val;   // put "14.10" back so next keystroke appends correctly
      }
    });
  }
  

  onKeydown(event: KeyboardEvent) {
    if (event.ctrlKey && event.metaKey) { return; }
    if (!/[0-9\/]/.test(event.key) && event.key.length === 1) {
      event.preventDefault();
    }
  }

  dateformat(event: Event) {
    const input = event.target as HTMLInputElement;
    let value = input.value;

    if (!value) return;
    const parts = value.split('/');
    if (parts.length !== 3) return;

    let [month, day, year] = parts.map(part => part.padStart(2, '0'));
    if (month.length === 1) month = '0' + month;
    if (day.length === 1) day = '0' + day;
    if (year.length === 2) year = '20' + year;
    input.value = `${month}/${day}/${year}`;
  }
}